import type { FormEvent, ReactNode } from "react";
import { useState } from "react";
import { createUseStyles } from "react-jss";
import type {
  AgreementDocument,
  AgreementSignature,
} from "../../../../common/src";
import { agreementFingerprint, legalNameSchema } from "../../../../common/src";
import type { VetBoatsTheme } from "../../theme";
import { Button } from "../shared/Button";
import { Field } from "../shared/Field";
import { Notice } from "../shared/Notice";
import { useFormStyles } from "../shared/styles";
import { AgreementView } from "./AgreementView";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  hint: { color: theme.color.muted, fontSize: 13, margin: "6px 0 0" },
  agree: { fontWeight: 600 },
}));

interface AgreementSignerProps {
  doc: AgreementDocument;
  /** Fields that belong to the same submission (shown above the agreement). */
  children?: ReactNode;
  submitLabel: string;
  pending: boolean;
  error: string | undefined;
  /** Receives the signature for exactly the text on screen; resolve false to keep the form. */
  onSign: (signature: AgreementSignature) => Promise<void> | void;
}

/**
 * The full agreement on its own, an unchecked agreement box, and a typed legal name.
 * "Sign" stays disabled until the reader has scrolled to the end.
 */
export const AgreementSigner = ({
  doc,
  children,
  submitLabel,
  pending,
  error,
  onSign,
}: AgreementSignerProps) => {
  const classes = useStyles();
  const form = useFormStyles();
  const [readToEnd, setReadToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const name = legalNameSchema.safeParse(signedName);
    if (!name.success) {
      setNameError(name.error.issues[0]?.message);
      return;
    }
    setNameError(undefined);
    if (!agreed || !readToEnd) {
      return;
    }
    await onSign({
      version: doc.version,
      textSha256: agreementFingerprint(doc),
      signedName: name.data,
      agreed: true,
    });
  };

  return (
    <form className={form.stack} onSubmit={(e) => void submit(e)} noValidate>
      {children}
      <div>
        <AgreementView doc={doc} onReadToEnd={() => setReadToEnd(true)} />
        <p className={classes.hint} aria-live="polite">
          {readToEnd
            ? "You've reached the end of the agreement."
            : "Scroll to the end of the agreement to continue."}
        </p>
      </div>
      <label className={`${form.checkbox} ${classes.agree}`}>
        <input
          type="checkbox"
          checked={agreed}
          disabled={!readToEnd}
          onChange={(e) => setAgreed(e.target.checked)}
        />
        <span>I have read and agree to the {doc.title}.</span>
      </label>
      <Field
        label="Type your full legal name to sign"
        htmlFor={`signature-${doc.kind}`}
        hint="This is your electronic signature."
        error={nameError}
      >
        <input
          id={`signature-${doc.kind}`}
          className={form.control}
          autoComplete="name"
          value={signedName}
          disabled={!readToEnd}
          onChange={(e) => setSignedName(e.target.value)}
        />
      </Field>
      {error && <Notice kind="error">{error}</Notice>}
      <div className={form.actions}>
        <Button
          type="submit"
          disabled={
            pending || !readToEnd || !agreed || signedName.trim().length < 2
          }
        >
          {pending ? "Signing…" : submitLabel}
        </Button>
      </div>
    </form>
  );
};
